import * as React from "react";
import * as _ from "lodash";
import { mapConfig } from "../utils/map-utils/map-config";

interface IReturn {
  clusters: ICluster[];
  makeClusters: (zoom: number) => void;
}
export interface ICluster {
  type: "point" | "cluster";
  x: number;
  y: number;
  data?: any;
  qty: number;
  id: string;
  ignore?: boolean;
}

export const useCluster: (points: ICluster[]) => IReturn = (points) => {
  const [clusters, setClusters] = React.useState<ICluster[]>([]);

  const findCollisions = React.useCallback(
    (x: number, y: number, r: number, circles: ICluster[]) => {
      const collisions = [];
      for (let i = 0; i < circles.length; i++) {
        const circle = circles[i];

        const distanceX = x - circle.x;
        const distanceY = y - circle.y;
        const distanceCenter = Math.sqrt(
          distanceX * distanceX + distanceY * distanceY
        );
        let collisionDistance = r;
        if (r) collisionDistance += r;
        if (distanceCenter <= collisionDistance) {
          collisions.push(circle);
        }
      }
      return collisions;
    },
    []
  );

  const makeClusters = React.useCallback(
    (zoom: number) => {
      // If at max zoom, don't cluster
      let pointsToCheck = points;

      const maxZoom = mapConfig.zoom.max;
      const minZoom = mapConfig.zoom.min;

      const zoomScale = maxZoom - minZoom;
      const currentScale = zoom - minZoom;

      const percentZoomed = 1 - currentScale / zoomScale;

      const radius = mapConfig.pointRadius * percentZoomed;

      // If nearly zoomed all the way in, don't cluster
      if (percentZoomed < 0.1) {
        setClusters(points);
        return;
      }
      // Draw circle around each point with r = 40
      // For each point, detect if circle collides with another point
      pointsToCheck.forEach((point) => {
        const collisions = findCollisions(
          point.x,
          point.y,
          radius,
          pointsToCheck.filter((point) => !point.ignore)
        );
        if (!point.ignore) {
          // If we find collisions...
          if (collisions.length > 1) {
            // Remove all colliding points from the points array
            collisions.forEach((collidingPoint) => {
              pointsToCheck = _.map(pointsToCheck, (item) => {
                if (item.id === collidingPoint.id) {
                  return {
                    ...item,
                    ignore: true,
                    qty: 1,
                  };
                } else {
                  return item;
                }
              });
            });

            // For each cluster, find centre point to render in the middle of child points
            const combined = collisions.reduce((a, b) => ({
              ...a,
              y: a.y + b.y,
              x: a.x + b.x,
              qty: a.qty + b.qty,
            }));

            const total = combined.qty;

            const centerX = combined.x / collisions.length;
            const centerY = combined.y / collisions.length;
            const cluster: ICluster = {
              x: centerX,
              y: centerY,
              id: `${point.id}-cluster`,
              type: "cluster",
              qty: total,
            };
            // Group points that collide as cluster
            pointsToCheck.push(cluster);
          }
        }
      });
      pointsToCheck = _.filter(pointsToCheck, (point) => {
        return !point.ignore;
      });
      setClusters(pointsToCheck);
    },
    [findCollisions, points]
  );

  return {
    clusters,
    makeClusters,
  };
};
